import { useNavigate, useSearch } from '@tanstack/react-router';

export interface CallDetailParam {
  /** Interaction whose Call detail sheet is open, or null. */
  openCallId: string | null;
  openCall: (interactionId: string) => void;
  closeCall: () => void;
}

/**
 * Read/write `?call=<interactionId>` — which call's detail sheet is open.
 *
 * ROUTE-AGNOSTIC on purpose. The Call detail sheet is opened from inside
 * `TaskWorkPanel`, which is hosted by both `/referrals` and `/tasks`
 * (and `/tasks/$taskId`), so this navigates with `to: '.'` — the route it is
 * already on — and preserves the rest of that route's search. Every hosting
 * route must declare `call` in its `validateSearch` schema, or the router
 * strips the param on write and the sheet never opens.
 *
 * `useSearch({ strict: false })` rather than a `from` path for the same
 * reason: there is no single route to name.
 */
export function useCallDetailParam(): CallDetailParam {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { call?: string };

  return {
    openCallId: search.call ?? null,
    openCall: (interactionId) => {
      // A push, so Back closes the sheet and returns to the referral or task
      // underneath rather than leaving the page.
      void navigate({ to: '.', search: (prev) => ({ ...prev, call: interactionId }) });
    },
    closeCall: () => {
      void navigate({ to: '.', search: (prev) => ({ ...prev, call: undefined }) });
    },
  };
}
